import {useEventBus} from '@vueuse/core'
import Echo from 'laravel-echo'
import {useNuxtApp} from 'nuxt/app'
import {defineStore} from 'pinia'
import Pusher from 'pusher-js'
import {useAuthStore} from './auth'
import {useNotification} from "~/composables/useNotification"

/**
 * Cross-Tab-Deduplizierung für Download-Events.
 *
 * Reverb liefert das `.report.generated`-Event an ALLE offenen Tabs
 * desselben Users aus. Ohne Absicherung würde jeder Tab die Datei
 * herunterladen (n Tabs = n Downloads).
 *
 * Lösung: navigator.locks (Web Locks API) serialisiert den Zugriff über
 * alle Tabs derselben Origin hinweg. In der kritischen Sektion prüfen
 * wir einen localStorage-Marker mit Zeitstempel (30s Cooldown). Der
 * erste Tab schreibt den Marker und lädt herunter; alle weiteren Tabs
 * sehen den Marker und skippen.
 *
 * HINWEIS: navigator.locks steht nur in Secure Contexts zur Verfügung
 * (https bzw. localhost). Auf lokalen http-Domains wie *.test greift
 * der Fallback (rein localStorage-basiert), der einen kleinen Race
 * hat und im schlimmsten Fall zu Mehrfach-Downloads pro Tab führt.
 * Für Produktion (https) ist das kein Problem.
 */
async function claimDownload(key: string): Promise<boolean> {
    const doClaim = (): boolean => {
        try {
            const existing = localStorage.getItem(key)
            if (existing && Date.now() - parseInt(existing, 10) < 30_000) {
                return false
            }
            localStorage.setItem(key, Date.now().toString())
            return true
        } catch (_) {
            return true
        }
    }

    if (typeof navigator !== 'undefined' && (navigator as any).locks?.request) {
        try {
            let result = false
            await (navigator as any).locks.request(
                'report-download-mutex',
                {mode: 'exclusive'},
                async () => {
                    result = doClaim()
                }
            )
            return result
        } catch (_) {
            return doClaim()
        }
    }

    return doClaim()
}

export const useWebsocketStore = defineStore('websockets', {
    state: () => ({
        channel: null,
        members: {},
        ready: false,
    }),

    getters: {
        /*userActive: (state) => {
            return (userId) => {


            }
        },*/
    },

    actions: {
        async init(mode = 'default') {
            // Idempotenter Init – HMR-fest UND race-fest.
            //
            // Wir dürfen NICHT this.ready als Wahrheit nehmen, weil Pinia im
            // Dev-Modus bei HMR den Store-State zurücksetzt, window.Echo aber
            // weiterlebt. Stattdessen markieren wir direkt am window-Objekt,
            // dass die Echo-Instanz aufgebaut wurde.
            //
            // WICHTIG: Das Flag wird SYNCHRON, ganz oben, gesetzt, damit
            // parallel mountende Layouts (default, service, container), die
            // init() im gleichen JS-Tick aufrufen, nicht alle den Guard
            // passieren und jeweils eine eigene Echo-Instanz samt
            // report.generated-Listener anlegen (-> mehrfache Downloads).
            if ((window as any).__echoInitialized) {
                if ((window as any).Echo) {
                    this.ready = true
                    if (mode === 'default') this.joinUserPrivate()
                }
                return
            }
            (window as any).__echoInitialized = true


            const config = useRuntimeConfig()
            window.Pusher = Pusher
            window.Echo = new Echo({
                broadcaster: 'reverb',
                key: config.public.websockets.key,
                host: config.public.websockets.host,
                httpHost: config.public.websockets.host,
                wsHost: config.public.websockets.host,
                cluster: config.public.websockets.cluster,
                forceTLS: config.public.websockets.forceTLS,
                disableStats: config.public.websockets.disableStats,
                enabledTransports: config.public.websockets.forceWSS ? ['wss'] : ['wss', 'ws'],
                authorizer: (channel: any, options: any) => {
                    return {
                        authorize: async (socketId: any, callback: any) => {
                            await $lara.post("/app/spa/broadcasting/auth", {
                                body: {
                                    socket_id: socketId,
                                    channel_name: channel.name
                                }
                            }).then((response) => {
                                callback(false, response)
                            }).catch(() => {
                                callback(true)
                            })
                        }
                    }
                }
            })

            // Set ready state to prevent undefined Echo
            this.ready = true

            if (mode === 'default') {
                this.joinUserPrivate()
            }

            window.Echo.connector.pusher.connection.bind('state_change', function (states: any) {
                if (states.current === 'disconnected') {
                    window.Echo.connector.pusher.connect()
                }
            })

            window.Echo.private('reports.' + useAuthStore().userData.id)
                .listen('.report.generated', async (event: any) => {
                    if (!event.generatedReportId) return

                    useEventBus('generatedReportsChanged').emit(event)

                    const shouldDownload = await claimDownload('report-download:' + event.generatedReportId)
                    if (!shouldDownload) return

                    $lara
                        .get(useNuxtApp().$apiRoute('spa.processes.generatedReports.download', {generatedReport: event.generatedReportId}), {
                            responseType: 'blob'
                        })
                        .then((response) => {
                            if (response) {
                                const url = window.URL.createObjectURL(new Blob([response]))
                                const link = document.createElement('a')
                                link.href = url
                                link.setAttribute('download', event.downloadFileName)
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                            }
                        })
                        .catch((error) => {
                            // The instant download is only one of two routes now. If it fails the
                            // report is still in the profile - say so instead of showing the raw
                            // error object to the user.
                            const status = error?.response?.status ?? -1

                            if (status === 422) {
                                useNotification().error(useI18n().t('users.generatedReports.liveDownloadInvalid'))
                            } else {
                                useNotification().error(
                                    useI18n().t('users.generatedReports.liveDownloadFailedTitle'),
                                    useI18n().t('users.generatedReports.liveDownloadFailedText')
                                )
                            }
                        })
                })
        },

        joinUserPrivate() {
            if (!useAuthStore().userData?.id) return
            window.Echo.private('App.Models.User.' + useAuthStore().userData.id)
                .notification((notification: any) => {
                    useEventBus('notifications').emit(notification)
                })
                .listen('UserSignatureCreated', (event: any) => {
                    useEventBus('userSignatureCreated').emit(event)
                })
                .error((error: Error) => {
                    useNotification().error(useI18n().t('error'), error.message)
                })
        },

        joinFormProcessPrivate(formProcessId: number) {
            window.Echo.channel(`formProcesses.${formProcessId}`)
                .listen('FormProcessSignatureCreated', (event: any) => {
                    useEventBus('formProcessSignatureCreated').emit(event)
                })
        },

        joinOrganisationPrivate(organisationId: number) {
            window.Echo.channel(`organisations.${organisationId}`)
                .listen('OrganisationSignatureCreated', (event: any) => {
                    useEventBus('organisationSignatureCreated').emit(event)
                })
        },

        leaveOrganisationPrivate(organisationId: number) {
            window.Echo.leave(`organisations.${organisationId}`)
        }
    }
})